import { useRef } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { FileUpload } from 'primereact/fileupload';
import type { FileUpload as FileUploadType } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressBar } from 'primereact/progressbar';
import type { DataIndexResponse } from '../api';
import type { StatusRecord } from '../ws';

interface Props {
  templates: string[];
  lookupKey: string;
  onLookupKeyChange: (key: string) => void;
  dataIndex: DataIndexResponse;
  selectedEntries: DataIndexResponse['entries'];
  onSelectedEntriesChange: (entries: DataIndexResponse['entries']) => void;
  selectedTemplates: string[];
  onSelectedTemplatesChange: (templates: string[]) => void;
  entryFilter: string;
  onEntryFilterChange: (filter: string) => void;
  statusFeed: StatusRecord[];
  busy: boolean;
  onDataIndexUpload: (files: File[]) => Promise<void>;
  onClearDataIndex: () => void;
  onGenerate: () => Promise<void>;
}

function StepBadge({ n, done }: { n: number; done: boolean }) {
  return (
    <span className={`step-badge${done ? ' step-badge--done' : ''}`}>
      {done ? <i className="pi pi-check" style={{ fontSize: '0.65rem' }} /> : n}
    </span>
  );
}

export function GeneratePage({
  templates,
  lookupKey,
  onLookupKeyChange,
  dataIndex,
  selectedEntries,
  onSelectedEntriesChange,
  selectedTemplates,
  onSelectedTemplatesChange,
  entryFilter,
  onEntryFilterChange,
  statusFeed,
  busy,
  onDataIndexUpload,
  onClearDataIndex,
  onGenerate,
}: Props) {
  const dataIndexUploadRef = useRef<FileUploadType | null>(null);

  const hasLookupKey = lookupKey.trim().length > 0;
  const hasDataIndex = dataIndex.dataIndexId !== '';
  const hasEntries = selectedEntries.length > 0;
  const hasTemplates = selectedTemplates.length > 0;
  const canGenerate = hasDataIndex && hasEntries && hasTemplates && !busy;

  const handleDataIndexUpload = async (event: { files: File[] }) => {
    await onDataIndexUpload(event.files);
    dataIndexUploadRef.current?.clear();
  };

  const entriesTableHeader = (
    <div className="flex align-items-center justify-content-between gap-2">
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          value={entryFilter}
          onChange={e => onEntryFilterChange(e.target.value)}
          placeholder="Search entries…"
          style={{ width: '220px' }}
        />
      </IconField>
      <div className="flex align-items-center gap-2">
        {selectedEntries.length > 0 && (
          <span style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600 }}>
            {selectedEntries.length} selected
          </span>
        )}
        {dataIndex.entries.length > 0 && (
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
            {dataIndex.entries.length} total
          </span>
        )}
      </div>
    </div>
  );

  const progressMsg = [...statusFeed].reverse().find(r => r.type === 'progress')?.message;

  function formatTime(d: Date): string {
    return d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  }

  function copyFeed() {
    const text = statusFeed
      .map(r => `[${formatTime(r.timestamp)}] ${r.message}`)
      .join('\n');
    navigator.clipboard.writeText(text);
  }

  return (
    <>
      {/* Step 1 — Lookup key */}
      <Card className="section-card">
        <div className="section-header">
          <div className="step-label">
            <StepBadge n={1} done={hasLookupKey} />
            <h3 className="m-0">Lookup Key</h3>
          </div>
          {hasLookupKey && (
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              <code style={{ background: '#f1f5f9', padding: '0.15rem 0.4rem', borderRadius: '0.25rem' }}>
                {lookupKey}
              </code>
            </span>
          )}
        </div>
        <span className="p-float-label" style={{ display: 'inline-block', marginTop: '0.75rem' }}>
          <InputText
            id="lookupKey"
            value={lookupKey}
            onChange={e => onLookupKeyChange(e.target.value)}
            style={{ width: '280px' }}
          />
          <label htmlFor="lookupKey">Column header(s), e.g. ID;TYPE</label>
        </span>
        <p className="text-500 text-sm mt-2 mb-0">
          Matches one or more column headers in the Excel file. Separate multiple keys with <code>;</code>.
        </p>
      </Card>

      {/* Step 2 — Data index */}
      <Card className="section-card">
        <div className="section-header">
          <div className="step-label">
            <StepBadge n={2} done={hasDataIndex} />
            <h3 className="m-0">Data Index</h3>
          </div>
          <div className="flex align-items-center gap-2">
            {hasDataIndex && (
              <Button
                label="Clear"
                icon="pi pi-times"
                size="small"
                severity="secondary"
                text
                onClick={onClearDataIndex}
              />
            )}
            <FileUpload
              ref={dataIndexUploadRef}
              mode="basic"
              name="file"
              chooseLabel={hasDataIndex ? 'Replace' : 'Upload .xlsx'}
              chooseOptions={{ icon: 'pi pi-upload' }}
              disabled={!hasLookupKey}
              auto
              customUpload
              uploadHandler={handleDataIndexUpload}
              accept=".xlsx"
            />
          </div>
        </div>

        {!hasLookupKey && (
          <p className="text-500 text-sm m-0">Enter a lookup key above before uploading.</p>
        )}

        {hasLookupKey && !hasDataIndex && (
          <p className="text-500 text-sm m-0">Upload an Excel file to load the data index.</p>
        )}

        {hasDataIndex && (
          <DataTable
            value={dataIndex.entries}
            selection={selectedEntries}
            onSelectionChange={e => onSelectedEntriesChange(e.value)}
            dataKey="lookupKey"
            selectionMode="checkbox"
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            header={entriesTableHeader}
            globalFilter={entryFilter}
            globalFilterFields={['lookupKey']}
            emptyMessage="No entries match the current filter."
          >
            <Column selectionMode="multiple" style={{ width: '3rem' }} />
            <Column field="lookupKey" header="Lookup Key" sortable />
          </DataTable>
        )}
      </Card>

      {/* Step 3 — Templates & Generate */}
      <Card className="section-card">
        <div className="section-header">
          <div className="step-label">
            <StepBadge n={3} done={hasEntries && hasTemplates} />
            <h3 className="m-0">Generate</h3>
          </div>
          <Button
            label={busy ? 'Generating…' : 'Generate zip'}
            icon={busy ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
            onClick={onGenerate}
            disabled={!canGenerate}
          />
        </div>

        <MultiSelect
          value={selectedTemplates}
          options={templates}
          onChange={e => onSelectedTemplatesChange(e.value)}
          placeholder={templates.length === 0 ? 'No templates — go to Templates page to upload' : 'Select templates to include…'}
          disabled={templates.length === 0}
          display="chip"
          style={{ width: '100%' }}
        />

        {/* Selection summary */}
        <div className="selection-summary mt-3">
          <span style={{ fontWeight: 600 }}>Ready to generate:</span>
          <span className={`selection-chip ${hasEntries ? 'selection-chip--entries' : 'selection-chip--empty'}`}>
            <i className={`pi ${hasEntries ? 'pi-check' : 'pi-times'}`} />
            {selectedEntries.length} {selectedEntries.length === 1 ? 'entry' : 'entries'}
          </span>
          <span style={{ color: '#94a3b8' }}>×</span>
          <span className={`selection-chip ${hasTemplates ? 'selection-chip--templates' : 'selection-chip--empty'}`}>
            <i className={`pi ${hasTemplates ? 'pi-check' : 'pi-times'}`} />
            {selectedTemplates.length} {selectedTemplates.length === 1 ? 'template' : 'templates'}
          </span>
          {hasEntries && hasTemplates && (
            <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
              = {selectedEntries.length * selectedTemplates.length} file{selectedEntries.length * selectedTemplates.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {busy && progressMsg && (
          <div className="mt-3">
            <ProgressBar mode="indeterminate" style={{ height: '4px' }} />
            <p className="text-500 text-sm mt-1 mb-0">{progressMsg}</p>
          </div>
        )}
      </Card>

      {/* Live status */}
      <Card className="section-card">
        <div className="section-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <h3 className="m-0">Live Status</h3>
            {statusFeed.length > 0 && (
              <span style={{
                background: '#e2e8f0', color: '#475569',
                borderRadius: '999px', fontSize: '0.75rem',
                fontWeight: 700, padding: '0.1rem 0.55rem',
              }}>
                {statusFeed.length}
              </span>
            )}
          </div>
          {statusFeed.length > 0 && (
            <Button
              icon="pi pi-copy"
              label="Copy"
              size="small"
              severity="secondary"
              text
              onClick={copyFeed}
              tooltip="Copy all to clipboard"
              tooltipOptions={{ position: 'left' }}
            />
          )}
        </div>
        <div className="status-feed" ref={el => { if (el) el.scrollTop = el.scrollHeight; }}>
          {statusFeed.length === 0 ? (
            <span className="status-feed-empty">No events yet.</span>
          ) : (
            statusFeed.map((record, idx) => (
              <div
                key={idx}
                className={`status-feed-line${record.type === 'progress-error' || record.type === 'generation-failed' ? ' status-feed-line--error' : record.type === 'generation-complete' ? ' status-feed-line--success' : ''}`}
              >
                <span className="status-feed-ts">{formatTime(record.timestamp)}</span>
                <span>{record.message}</span>
              </div>
            ))
          )}
        </div>
      </Card>
    </>
  );
}
